import React from 'react';

interface Props {
  children: React.ReactNode;
}

interface State {
  hasError: boolean;
  isChunkError: boolean;
  error: Error | null;
}

// Heuristic: dynamic-import failures surface as errors whose name or message
// mentions chunks. We treat them as transient (stale deploy / network blip)
// and offer a reload path rather than the full error UI.
function isChunkLoadError(error: Error): boolean {
  if (error.name === 'ChunkLoadError') return true;
  const message = error.message || '';
  return (
    message.includes('Loading chunk') ||
    message.includes('Failed to fetch dynamically imported module') ||
    message.includes('Importing a module script failed')
  );
}

export class RoutingErrorBoundary extends React.Component<Props, State> {
  state: State = { hasError: false, isChunkError: false, error: null };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, isChunkError: isChunkLoadError(error), error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error('[RoutingErrorBoundary]', error, info);
  }

  private handleReload = () => {
    window.location.reload();
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    const title = this.state.isChunkError
      ? 'Nova versão disponível'
      : 'Erro ao carregar tela';
    const message = this.state.isChunkError
      ? 'Uma versão mais recente do app foi publicada. Recarregue para continuar.'
      : 'Não foi possível renderizar esta tela. Tente recarregar ou volte e abra novamente.';

    return (
      <div
        role="alert"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '60vh',
          padding: 24,
        }}
      >
        <div
          style={{
            background: 'white',
            borderRadius: 16,
            padding: 32,
            maxWidth: 440,
            width: '100%',
            boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
            textAlign: 'center',
          }}
        >
          <h2 style={{ margin: '0 0 8px', fontSize: 18, fontWeight: 700, color: '#111827' }}>
            {title}
          </h2>
          <p style={{ margin: '0 0 20px', color: '#6b7280', fontSize: 14, lineHeight: 1.5 }}>
            {message}
          </p>
          {this.state.error && !this.state.isChunkError ? (
            <pre
              style={{
                background: '#f9fafb',
                border: '1px solid #e5e7eb',
                borderRadius: 8,
                padding: 12,
                fontSize: 11,
                color: '#dc2626',
                textAlign: 'left',
                overflow: 'auto',
                maxHeight: 120,
                marginBottom: 20,
                whiteSpace: 'pre-wrap',
              }}
            >
              {this.state.error.message}
            </pre>
          ) : null}
          <button
            onClick={this.handleReload}
            style={{
              padding: '10px 20px',
              background: '#154680',
              color: 'white',
              border: 'none',
              borderRadius: 8,
              fontSize: 14,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Recarregar
          </button>
        </div>
      </div>
    );
  }
}
